/**
 * `wpp::tabs`
 *
 * The roving `tabindex` was already right — one tab in the tab order, the rest
 * at `-1` — but nothing moved between them, so every tab except the selected
 * one was unreachable. Left and Right (and Home and End) are what a roving
 * tabindex is for; without them it is only a way of hiding controls.
 *
 * Panels are wired up here rather than in the markup. A `role="tab"` is
 * supposed to say which panel it controls, and `wpp::tabs.tab` cannot know
 * whether a `wpp::tabs.panel` exists for it — pointing `aria-controls` at an id
 * that is not on the page is worse than leaving it off. So the link is made at
 * runtime, when both ends are known to be there.
 */
export default (localActiveTab = null) => ({
    localActiveTab,
    tabWidth: '0',
    tabTransform: '0',

    init() {
        let key = this.localActiveTab;

        if (this.localActiveTab === null || this.localActiveTab === '') {
            key = this.$refs.tablist.children[0].id;
        }

        this.$nextTick(() => {
            this.select(key);
            this.linkPanels();
        });

        /*
         * A bar that boots hidden — inside a slide-over still on its way in, a
         * collapsed section — measures every tab at nought, and the slider
         * stays a zero-width line under the selected tab until somebody clicks
         * another. Measuring again whenever the bar's size changes covers that
         * and a resized window both.
         */
        if (typeof ResizeObserver !== 'undefined') {
            this.resizeObserver = new ResizeObserver(() => this.measure());
            this.resizeObserver.observe(this.$refs.tablist);
        }
    },

    destroy() {
        this.resizeObserver?.disconnect();
    },

    /** The tabs, in the order they are laid out. */
    tabs() {
        return Array.from(this.$refs.tablist.querySelectorAll('[role="tab"]'));
    },

    /**
     * Point each tab at its panel, where one was rendered.
     */
    linkPanels() {
        this.tabs().forEach((tab) => {
            const panel = document.getElementById(`${tab.id}-panel`);

            if (panel) {
                tab.setAttribute('aria-controls', panel.id);
            }
        });
    },

    onKeydown(event) {
        const tabs = this.tabs();
        const index = tabs.indexOf(event.target);

        if (index === -1) {
            return;
        }

        const target = {
            ArrowRight: tabs[(index + 1) % tabs.length],
            ArrowLeft: tabs[(index - 1 + tabs.length) % tabs.length],
            Home: tabs[0],
            End: tabs[tabs.length - 1],
        }[event.key];

        if (! target) {
            return;
        }

        event.preventDefault();

        /*
         * Selection follows focus, which is the right choice for tabs whose
         * panels are already on the page: arrowing along the bar shows each one
         * as you reach it, and there is no second key to press.
         */
        this.select(target.id);
        target.focus();
    },

    select(id) {
        const tabEl = document.getElementById(id);

        /*
         * An id that resolves to nothing is not a crash. It happens whenever the
         * selected tab is removed or renamed while the component is alive — a
         * `wire:model` pointing at a tab that a re-render dropped, say — and
         * reading `offsetWidth` off the null threw, taking the whole Alpine tree
         * down with it.
         */
        if (! tabEl) {
            return;
        }

        this.localActiveTab = id;
        this.activeTab = id;
        this.measure();
    },

    /** Put the slider under the selected tab, at the size it is drawn now. */
    measure() {
        const tabEl = document.getElementById(this.localActiveTab);

        if (! tabEl) {
            return;
        }

        this.tabWidth = tabEl.offsetWidth + 'px';
        this.tabTransform = tabEl.offsetLeft + 'px';
    },

    isSelected(id) {
        return this.localActiveTab === id;
    },

    container: {
        [':style']() {
            return `--tab-bar-item-width: ${this.tabWidth}; --tab-bar-item-transform: ${this.tabTransform};`;
        }
    },
    tab: {
        ['x-on:click']() {
            this.select(this.$el.id);
        },
        [':tabindex']() {
            return this.isSelected(this.$el.id) ? 0 : -1;
        },
        [':aria-selected']() {
            return this.isSelected(this.$el.id);
        },
        [':class']() {
            return this.isSelected(this.$el.id) ? 'text-brand-500' : 'text-wpp-gray-800';
        },
        ['@mousedown.prevent']: '',
    }
});
